// Single source of truth for invoice document-type semantics on the frontend.
//
// Mirrors api/src/invoices/doc-type.ts (the two packages can't share a module).
// `docType` carries historical synonyms for the same accounting direction
// (payables/bill/expense = AP "you pay", receivables/invoice = AR "you send"),
// plus purchase_order and credit/debit notes. We do NOT collapse the stored
// values — bill/expense/invoice map to distinct QBO/Xero entities — so every UI
// read site asks these helpers about direction/kind and renders labels from the
// shared map below, keeping the upload screen and the doc-type dropdown
// consistent.

export type DocType =
  | "invoice"
  | "bill"
  | "expense"
  | "payables"
  | "receivables"
  | "creditNote"
  | "debitNote"
  | "purchase_order";

const PAYABLE = new Set<string>(["payables", "bill", "expense"]);
const RECEIVABLE = new Set<string>(["receivables", "invoice"]);

export const PURCHASE_ORDER_DOC_TYPE = "purchase_order";

export function normalizeDocType(value: unknown): string {
  if (value == null) return "";
  const v = String(value).trim();
  if (!v) return "";
  const lower = v.toLowerCase();
  if (lower === "purchaseorder" || lower === "purchase order" || lower === "purchase_order") {
    return PURCHASE_ORDER_DOC_TYPE;
  }
  return v;
}

export function isPayable(value: unknown): boolean {
  return PAYABLE.has(normalizeDocType(value));
}
export function isReceivable(value: unknown): boolean {
  return RECEIVABLE.has(normalizeDocType(value));
}
export function isPurchaseOrder(value: unknown): boolean {
  return normalizeDocType(value) === PURCHASE_ORDER_DOC_TYPE;
}

// Human labels for every doc-type value. Integration-specific kinds keep their
// entity name (Bill/Expense/Sales Invoice); the generic Excel directions read as
// the paired "Bills/Payables" / "Invoices/Receivables".
export const DOC_TYPE_LABELS: Record<string, string> = {
  // QuickBooks/Xero entity kinds
  expense: "Expense",
  invoice: "Sales Invoice",
  bill: "Bill",
  // Generic Excel directions
  payables: "Bills/Payables",
  receivables: "Invoices/Receivables",
  // Notes + PO
  creditNote: "Credit Note",
  debitNote: "Debit Note",
  purchase_order: "Purchase Order",
};

export function docTypeLabel(value: unknown): string {
  const v = normalizeDocType(value);
  return DOC_TYPE_LABELS[v] || v;
}

/**
 * The selectable document-type options for the doc-type dropdown, by export
 * target. Purchase Order is integration-agnostic (marks the doc as a local match
 * candidate, never synced) and is always offered so the option set matches the
 * upload screen.
 */
export function getDocumentTypeOptions(opts: { exportTo?: string; isXero?: boolean }): string[] {
  if (opts.exportTo !== "qbo") {
    return ["payables", "receivables", "creditNote", "debitNote", "purchase_order"];
  }
  if (opts.isXero) {
    return ["invoice", "bill", "creditNote", "debitNote", "purchase_order"];
  }
  // QuickBooks
  return ["expense", "invoice", "bill", "creditNote", "purchase_order"];
}
